import React, { useState, useEffect } from 'react';
import Container from '../components/common/Container';
import VocabularySearch from '../components/CorporateVocabulary/VocabularySearch';
import VocabularyFilters from '../components/CorporateVocabulary/VocabularyFilters';
import VocabularyTermCard from '../components/CorporateVocabulary/VocabularyTermCard';
import { corporateVocabularyService, CorporateVocabularyTerm } from '../api/services/corporateVocabulary';

const CorporateVocabulary: React.FC = () => {
  const [terms, setTerms] = useState<CorporateVocabularyTerm[]>([]);
  const [filteredTerms, setFilteredTerms] = useState<CorporateVocabularyTerm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState<string>('');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('');
  const [availableIndustries, setAvailableIndustries] = useState<string[]>([]);
  const [availableDifficultyLevels, setAvailableDifficultyLevels] = useState<string[]>([]);

  useEffect(() => {
    fetchTerms();
    fetchFilters();
  }, []);

  useEffect(() => {
    filterTerms();
  }, [terms, searchTerm, selectedIndustry, selectedDifficulty]);

  const fetchTerms = async () => {
    try {
      setLoading(true);
      const response = await corporateVocabularyService.getAllTerms();

      if (response.success && Array.isArray(response.data)) {
        setTerms(response.data);
        setFilteredTerms(response.data);
        setError(null);
      } else {
        console.error('API returned unexpected data type:', typeof response.data);
        setError('Failed to fetch vocabulary terms: Invalid data format');
      }
    } catch (error) {
      console.error('Error fetching terms:', error);
      setError('Failed to fetch vocabulary terms');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const [industriesResponse, difficultyResponse] = await Promise.all([
        corporateVocabularyService.getIndustries(),
        corporateVocabularyService.getDifficultyLevels()
      ]);

      if (industriesResponse.success && Array.isArray(industriesResponse.data)) {
        setAvailableIndustries(industriesResponse.data);
      }

      if (difficultyResponse.success && Array.isArray(difficultyResponse.data)) {
        setAvailableDifficultyLevels(difficultyResponse.data);
      }
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  };

  const filterTerms = () => {
    let filtered = [...terms];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(term =>
        term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
        term.definition.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (term.context && term.context.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply industry filter
    if (selectedIndustry) {
      filtered = filtered.filter(term => term.industry === selectedIndustry);
    }

    // Apply difficulty filter
    if (selectedDifficulty) {
      filtered = filtered.filter(term => term.difficulty_level === selectedDifficulty);
    }

    setFilteredTerms(filtered);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedIndustry('');
    setSelectedDifficulty('');
  };

  if (loading) {
    return (
      <Container>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading vocabulary terms...</p>
          </div>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Error Loading Terms</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
            <button
              onClick={fetchTerms}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Corporate Vocabulary</h1>
          <h2 className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mb-3">
            Your Vocabulary is Your Competitive Weapon
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Master essential business and technology terminology. Click any term to explore definitions, examples, and insights.
          </p>
        </div>

                {/* Search and Filters */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center">
            <div className="flex-1 min-w-0">
              <VocabularySearch
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search terms, definitions, or context..."
              />
            </div>

            <div className="flex-shrink-0">
              <VocabularyFilters
                industries={availableIndustries}
                difficultyLevels={availableDifficultyLevels}
                selectedIndustry={selectedIndustry}
                selectedDifficulty={selectedDifficulty}
                onIndustryChange={setSelectedIndustry}
                onDifficultyChange={setSelectedDifficulty}
                onClearFilters={clearFilters}
              />
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mb-6 flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {filteredTerms.length} of {terms.length} terms
          </div>
          {(searchTerm || selectedIndustry || selectedDifficulty) && (
            <button
              onClick={clearFilters}
              className="text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 text-sm font-medium"
            >
              Clear all filters
            </button>
          )}
        </div>

        {/* Terms Grid */}
        {filteredTerms.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTerms.map((term) => (
              <VocabularyTermCard key={term.id} term={term} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-gray-400 dark:text-gray-500 text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No terms found</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {(searchTerm || selectedIndustry || selectedDifficulty)
                ? "Try adjusting your search or filters to find what you're looking for."
                : "No vocabulary terms are available yet. Check back soon!"}
            </p>
          </div>
        )}
      </div>
    </Container>
  );
};

export default CorporateVocabulary;
